/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./Admin.css";
import PropTypes from "prop-types";
import { API_ENDPOINT, MAIN_DOMAIN, PLATFORM } from "../../config";
import { simpleFetch } from "../../restWrapper";
import errorMsg from "../../assets/json/errMsg.json";
import { renderErrorMsg } from "../../assets/component/renderErrorMsg.js";
import { renderOrgLanguageSelector } from "../../assets/component/renderOrgLanguageSelector.js";
import FileViewer from "../AccountOverview/FileViewer";

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return d.getFullYear() + "/" + mm + "/" + dd;
}

function statusKeyOf(organizationPermitted) {
  if (organizationPermitted === "approved") return "approved";
  if (organizationPermitted === "pending") return "pending";
  return "declined";
}

function statusBadgeClass(key) {
  if (key === "approved") return "Admin-badgeOk";
  if (key === "pending") return "Admin-badgeWarn";
  return "Admin-badgeBad";
}

function statusIndicatorClass(key) {
  if (key === "approved") return "Admin-activeIndicator";
  if (key === "pending") return "Admin-pendingIndicator";
  return "Admin-suspendedIndicator";
}

// CP-299 — two switches, pointing opposite ways.
//
// PLUG_ELIGIBLE_FIELD ("List on Plug") belongs to staff and defaults to
// false: the rollout is curated, one organization at a time, so nothing is
// listed until the team switches it on.
//
// PLUG_OPT_IN_FIELD ("Enable on Plug") belongs to the organization and
// defaults to *true*: it is a veto, not a request. A nonprofit staff have
// listed stays listed unless it withdraws from its own settings (CP-300).
// Because the default is on, this gates nothing today while still being
// honoured the moment CP-300 gives nonprofits a way to switch it off — and
// no organization has to act for the rollout to proceed.
// Both names track CP-271's backend model, so a rename stays one edit.
const PLUG_ELIGIBLE_FIELD = "plugListingEligible";
const PLUG_OPT_IN_FIELD = "plugListingOptIn";

// Default-on means *absent* counts as enabled, not just an explicit true.
// Every organization predating the field has no value at all, and none of
// them have withdrawn.
function orgEnabledPlug(organizationObj) {
  return organizationObj[PLUG_OPT_IN_FIELD] !== false;
}

// Approval is a precondition rather than a switch — an org that is pending or
// declined can never be listed, however the other two are set.
function plugListingStateOf(organizationObj) {
  if (!organizationObj) return "blocked";
  if (organizationObj.organizationPermitted !== "approved") return "blocked";
  if (!organizationObj[PLUG_ELIGIBLE_FIELD]) return "ineligible";
  if (!orgEnabledPlug(organizationObj)) return "disabledByOrg";
  return "listed";
}

function plugBadgeClass(state) {
  if (state === "listed") return "Admin-badgeOk";
  // Staff listed them and the nonprofit withdrew — not a fault, but the one
  // state where the two switches disagree, so it should not read as neutral.
  if (state === "disabledByOrg") return "Admin-badgeWarn";
  return "Admin-badgeMuted";
}

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // auto sign out after 15 minutes idle
const IDLE_WARNING_SECONDS = 60; // warn this many seconds before signing out
const IDLE_ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
];

class Admin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      username: "",
      orgState: "approved",
      orgList: [],
      loaded: false,
      selectedStatus: "",
      plugListingFilter: "",
      searchQuery: "",
      sortBy: "recent",
      files: [],
      filesLoading: false,
      docsBusy: false,
      docsErrMsg: null,
      fileViewerUrl: null,
      fileViewerName: null,
      showIdleWarning: false,
      idleCountdown: IDLE_WARNING_SECONDS,
      languageObj: this.props.languageObj,
    };
    this.onLogoutClick = this.onLogoutClick.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.selectOnClick = this.selectOnClick.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
    this.onPlugListingFilterChange = this.onPlugListingFilterChange.bind(this);
    this.resetIdleTimer = this.resetIdleTimer.bind(this);
    this.onStaySignedIn = this.onStaySignedIn.bind(this);
    this.onIdleLogout = this.onIdleLogout.bind(this);
    this.fetchOrgFiles = this.fetchOrgFiles.bind(this);
    this.onDocUpload = this.onDocUpload.bind(this);
    this.onDocDelete = this.onDocDelete.bind(this);
  }

  componentDidMount() {
    this.fetchData();
    this.resetIdleTimer();
    IDLE_ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, this.resetIdleTimer),
    );
  }

  componentWillUnmount() {
    IDLE_ACTIVITY_EVENTS.forEach((evt) =>
      window.removeEventListener(evt, this.resetIdleTimer),
    );
    clearTimeout(this._idleWarnTimer);
    clearTimeout(this._idleLogoutTimer);
    clearInterval(this._idleCountdownInterval);
  }

  resetIdleTimer() {
    // Once the warning is showing, only the explicit "stay signed in" click
    // (which also calls this) should reset it — ambient mouse movement
    // shouldn't silently dismiss a warning nobody has actually noticed.
    if (this.state.showIdleWarning) return;

    clearTimeout(this._idleWarnTimer);
    clearTimeout(this._idleLogoutTimer);

    this._idleWarnTimer = setTimeout(() => {
      this.setState({
        showIdleWarning: true,
        idleCountdown: IDLE_WARNING_SECONDS,
      });
      this._idleCountdownInterval = setInterval(() => {
        this.setState((prevState) => {
          if (prevState.idleCountdown <= 1) {
            clearInterval(this._idleCountdownInterval);
            return { idleCountdown: 0 };
          }
          return { idleCountdown: prevState.idleCountdown - 1 };
        });
      }, 1000);
    }, IDLE_TIMEOUT_MS - IDLE_WARNING_SECONDS * 1000);

    this._idleLogoutTimer = setTimeout(this.onIdleLogout, IDLE_TIMEOUT_MS);
  }

  onStaySignedIn() {
    clearInterval(this._idleCountdownInterval);
    this.setState({ showIdleWarning: false }, this.resetIdleTimer);
  }

  onIdleLogout() {
    clearInterval(this._idleCountdownInterval);
    this.setState({ showIdleWarning: false });
    this.props.logout();
  }

  fetchData() {
    if (this.state.fetching) return false;
    this.setState({
      ...this.state,
      ...{
        fetching: true,
      },
    });
    let findParams = {};
    if (this.state.selectedStatus) {
      findParams = {
        organizationPermitted: this.state.selectedStatus,
      };
    }
    //Get orgs' data from bankend
    simpleFetch("/staffs/organizations", "GET", { findParams }).then((res) => {
      if (res.body.length === 0) {
        this.setState({
          fetching: false,
          loaded: true,
        });
        return false;
      }
      var orgsData = res.body;
      for (var i = 0; i < orgsData.length; i++) {
        orgsData[i].clicked = false;
        const bankStatus = orgsData[i].organizationObj.bankSettingStatus;
        if (typeof bankStatus !== "boolean") {
          //Set default bank setting status
          orgsData[i].organizationObj.bankSettingStatus = true; //default is true
        }
      }
      this.setState({
        fetching: false,
        loaded: true,
        orgList: orgsData,
      });
    });
  }

  selectOnClick(e) {
    if (this.state.fetching) return false;

    if (this.state.selectedStatus === e.target.value) return false;

    this.setState({
      ...this.state,
      ...{
        selectedStatus: e.target.value,
        files: [],
      },
    });
  }

  onSearchChange(e) {
    this.setState({ searchQuery: e.target.value });
  }

  onSortChange(e) {
    this.setState({ sortBy: e.target.value });
  }

  onPlugListingFilterChange(e) {
    this.setState({ plugListingFilter: e.target.value });
  }

  getVisibleOrgs() {
    const { orgList, selectedStatus, plugListingFilter, searchQuery, sortBy } =
      this.state;
    const query = searchQuery.trim().toLowerCase();

    const filtered = orgList.filter((data) => {
      const orgState = data.organizationObj.organizationPermitted;
      if (selectedStatus !== "" && selectedStatus !== orgState) return false;
      // Listing state is derived from two fields rather than stored, so this
      // filter stays client-side — the status filter above is the only one
      // the /staffs/organizations query understands.
      if (
        plugListingFilter !== "" &&
        plugListingFilter !== plugListingStateOf(data.organizationObj)
      ) {
        return false;
      }
      if (query) {
        const name = (data.organizationObj.organizationName || "")
          .toLowerCase();
        const email = (data.username || "").toLowerCase();
        if (!name.includes(query) && !email.includes(query)) return false;
      }
      return true;
    });

    const sorted = filtered.slice();
    if (sortBy === "name") {
      sorted.sort((a, b) =>
        (a.organizationObj.organizationName || "").localeCompare(
          b.organizationObj.organizationName || "",
        ),
      );
    } else if (sortBy === "status") {
      sorted.sort((a, b) =>
        (a.organizationObj.organizationPermitted || "").localeCompare(
          b.organizationObj.organizationPermitted || "",
        ),
      );
    }
    return sorted;
  }

  resetItemsColor() {
    let items = this.state.orgList;
    for (var i = 0; i < items.length; i++) {
      items[i].clicked = false;
    }
    this.setState({
      orgList: items,
    });
  }

  onLogoutClick() {
    this.props.logout();
  }

  // Load the selected org's documents. Only the most recently selected org's
  // response is applied, so a slow, superseded request can't overwrite a
  // newer selection.
  fetchOrgFiles(orgId) {
    this._activeFilesOrgId = orgId;
    this.setState({ files: [], filesLoading: true });
    return simpleFetch("/staffs/organizations/" + orgId + "/files", "GET", {})
      .then((res) => {
        if (this._activeFilesOrgId !== orgId) return;
        this.setState({
          filesLoading: false,
          files: res && Array.isArray(res.body) && res.body.length > 0
            ? res.body
            : [],
        });
      })
      .catch(() => {
        if (this._activeFilesOrgId !== orgId) return;
        this.setState({ filesLoading: false });
      });
  }

  docsErrorPack() {
    const pack = errorMsg[this.props.language] || errorMsg.en;
    return pack.Admin;
  }

  // Staff put a legal document on file for the org (CP-421). PDF only, to
  // match what the org itself can upload on OrgSetup.
  onDocUpload(orgId, e) {
    const input = e.target;
    const file = input.files && input.files[0];
    // Clear the input so choosing the same file again still fires onChange
    input.value = "";
    if (!file || this.state.docsBusy) return;

    const messages = this.docsErrorPack();
    if (file.type !== "application/pdf") {
      this.setState({ docsErrMsg: messages.docPdfOnly });
      return;
    }

    const data = new FormData();
    data.append("file", file);
    this.setState({ docsBusy: true, docsErrMsg: null });
    simpleFetch("/staffs/organizations/" + orgId + "/files", "POST", data, true)
      .then((res) => {
        const status = res && res.status;
        if (status === 200) {
          this.setState({ docsBusy: false });
          if (this._activeFilesOrgId === orgId) this.fetchOrgFiles(orgId);
          return;
        }
        this.setState({
          docsBusy: false,
          docsErrMsg:
            status === 413
              ? messages.docTooLarge
              : status === 415
                ? messages.docPdfOnly
                : messages.docUploadFail,
        });
      });
  }

  onDocDelete(orgId, file, e) {
    // The card itself opens the viewer
    e.stopPropagation();
    if (this.state.docsBusy) return;

    const actions = this.props.languageObj.orgDocActions;
    if (!window.confirm(actions.confirmDelete.replace("{name}", file.filename))) {
      return;
    }

    this.setState({ docsBusy: true, docsErrMsg: null });
    simpleFetch(
      "/staffs/organizations/" + orgId + "/files/" + file._id,
      "DELETE",
      {},
    ).then((res) => {
      if (res && res.status === 204) {
        this.setState({ docsBusy: false });
        if (this._activeFilesOrgId === orgId) this.fetchOrgFiles(orgId);
        return;
      }
      this.setState({
        docsBusy: false,
        docsErrMsg: this.docsErrorPack().docDeleteFail,
      });
    });
  }

  doc(func, file, orgId) {
    const key = file._id + file.filename;
    const ext = (file.filename.split(".").pop() || "").toUpperCase().slice(0, 4);
    const actions = func.props.languageObj.orgDocActions;

    function viewDoc() {
      if (!file._id) return;
      const link =
        API_ENDPOINT + "/staffs/organizations/" + orgId + "/files/" + file._id;
      func.setState({ fileViewerUrl: link, fileViewerName: file.filename });
    }

    return (
      <div className="Admin-doc" key={key} onClick={viewDoc}>
        <div className="Admin-docThumb">
          <span>{ext}</span>
        </div>
        <div className="Admin-docText">
          <div className="Admin-docName">{file.filename}</div>
          <div className="Admin-docHint">View</div>
        </div>
        <button
          type="button"
          className="Admin-docDelete"
          aria-label={actions.delete + " " + file.filename}
          title={actions.delete}
          disabled={func.state.docsBusy}
          onClick={(e) => func.onDocDelete(orgId, file, e)}
        >
          <svg className="Admin-btnIcon" viewBox="0 0 24 24">
            <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12h10l1-12M9 7V4h6v3" />
          </svg>
        </button>
      </div>
    );
  }

  renderOrgDocs(orgId) {
    const languageObj = this.props.languageObj;
    if (!this.state.files) return null;

    const actions = languageObj.orgDocActions;

    return (
      <div className="Admin-section">
        <div className="Admin-sectionHead">
          <h3>{languageObj.OrgDocs[1]}</h3>
          <label
            className={
              "Admin-btn Admin-docUpload" +
              (this.state.docsBusy ? " Admin-docUpload--busy" : "")
            }
          >
            {this.state.docsBusy ? actions.working : actions.upload}
            <input
              type="file"
              accept="application/pdf"
              disabled={this.state.docsBusy}
              onChange={(e) => this.onDocUpload(orgId, e)}
            />
          </label>
        </div>
        {this.state.filesLoading ? (
          <div className="Admin-docList">
            {[0, 1].map((i) => (
              <div className="Admin-doc Admin-docSkeleton" key={"docSkeleton" + i}>
                <div className="Admin-skeleton Admin-docSkeletonThumb" />
                <div className="Admin-docSkeletonLines">
                  <div className="Admin-skeleton Admin-docSkeletonLine Admin-docSkeletonLine--name" />
                  <div className="Admin-skeleton Admin-docSkeletonLine Admin-docSkeletonLine--hint" />
                </div>
              </div>
            ))}
          </div>
        ) : this.state.files.length < 1 ? (
          <div className="Admin-emptyList">{languageObj.OrgDocs[0]}</div>
        ) : (
          <div className="Admin-docList">
            {this.state.files.map((file) => this.doc(this, file, orgId))}
          </div>
        )}
        {this.state.docsErrMsg && renderErrorMsg(this.state.docsErrMsg)}
      </div>
    );
  }

  renderOrgDetails(func) {
    let items = this.state.orgList;
    let item;
    let arrayNo;
    for (var i = 0; i < items.length; i++) {
      if (items[i].clicked) {
        item = items[i];
        arrayNo = i;
      }
    }

    if (!item) {
      return (
        <div className="Admin-emptyDetail">
          {func.props.languageObj.selectPrompt}
        </div>
      );
    }

    const languageObj = func.props.languageObj;

    function changeOrgStatus(s) {
      if (
        func.state.fetching ||
        s === item.organizationObj.organizationPermitted
      )
        return false;
      func.setState({
        ...func.state,
        ...{
          fetching: true,
        },
      });
      let r = item.organizationObj.organizationPermittedReason;
      const bankSettingStatus = item.organizationObj.bankSettingStatus;
      let obj = !r
        ? { organizationPermitted: s }
        : {
          organizationPermitted: s,
          organizationPermittedReason: r,
          bankSettingStatus: bankSettingStatus,
        };

      simpleFetch("/staffs/organizations/" + item.id, "PUT", obj).then(
        (res) => {
          func.setState({
            ...func.state,
            ...{
              fetching: false,
            },
          });
          if (res.status === 204) {
            window.location.reload(false);
          } else {
            this.setState({
              errMsg: errorMsg[this.state.language].fail,
            });
          }
        },
      );
    }

    function onStatusChange(e) {
      changeOrgStatus(e.target.value);
    }
    function onApproveClick() {
      changeOrgStatus("approved");
    }
    function onDeclineClick() {
      changeOrgStatus("declined");
    }
    function inputChange(e) {
      items[arrayNo].organizationObj.organizationPermittedReason =
        e.target.value;
      func.setState({
        orgList: items,
      });
    }
    const onClickCheckBox = () => {
      const bankStatus = !items[arrayNo].organizationObj.bankSettingStatus;
      items[arrayNo].organizationObj.bankSettingStatus = bankStatus;
      func.setState({
        orgList: items,
        fetching: true,
      });

      simpleFetch("/staffs/organizations/" + items[arrayNo].id, "PUT", {
        bankSettingStatus: bankStatus,
      }).then((res) => {
        func.setState({
          ...func.state,
          ...{
            fetching: false,
          },
        });
      });
    };

    const plugListingFailMsg = () => {
      const pack = errorMsg[func.props.language] || errorMsg.en;
      return { plugListing: pack.Admin.plugListingFail };
    };

    // Unlike the bank toggle above, this one reverts on a failed write. A
    // listing flag that looks set but never reached the backend would have
    // staff believing an org is cleared for Plug when it is not.
    const onPlugEligibleToggle = () => {
      if (func.state.fetching) return false;

      const next = !items[arrayNo].organizationObj[PLUG_ELIGIBLE_FIELD];
      const revert = () => {
        items[arrayNo].organizationObj[PLUG_ELIGIBLE_FIELD] = !next;
        func.setState({
          orgList: items,
          fetching: false,
          errMsg: plugListingFailMsg(),
        });
      };

      items[arrayNo].organizationObj[PLUG_ELIGIBLE_FIELD] = next;
      func.setState({ orgList: items, fetching: true, errMsg: null });

      simpleFetch("/staffs/organizations/" + items[arrayNo].id, "PUT", {
        [PLUG_ELIGIBLE_FIELD]: next,
      })
        .then((res) => {
          if (res.status === 204) {
            func.setState({ fetching: false });
            return;
          }
          revert();
        })
        .catch(revert);
    };

    const fields = [];
    let reasonEl = null;

    Object.keys(item).forEach(function (key) {
      if (key === "organizationObj") {
        var dataObj = item[key];
        Object.keys(dataObj).forEach(function (i) {
          if (
            i === "organizationName" ||
            i === "organizationPermitted"
          ) {
            return; // shown as the page title / status badge instead
          } else if (i === PLUG_ELIGIBLE_FIELD || i === PLUG_OPT_IN_FIELD) {
            // Both are presented together in the Plug marketplace section,
            // where the interaction between them can be explained. Falling
            // through to the generic branch would print them twice, as bare
            // "true" / "false" rows.
            return;
          } else if (i === "organizationPermittedReason") {
            if (item.organizationObj.organizationPermitted === "declined") {
              reasonEl = (
                <div className="Admin-section" key="reason">
                  <h3>{languageObj[i]}</h3>
                  <div className="Admin-memotextFieldContainer">
                    <textarea
                      className="Admin-memotextField"
                      value={dataObj[i]}
                      onChange={inputChange}
                    />
                  </div>
                </div>
              );
            }
            return;
          } else if (i === "agreementDate") {
            fields.push(
              <div className="Admin-field" key={i}>
                <strong>{languageObj[i]}</strong>
                <div className="Admin-fieldValue">
                  {formatDate(dataObj[i]) || "—"}
                </div>
              </div>,
            );
          } else if (i === "completedSetup") {
            fields.push(
              <div className="Admin-field" key={i}>
                <strong>{languageObj[i]}</strong>
                <div className="Admin-fieldValue">
                  <span
                    className={
                      "Admin-badge " +
                      (dataObj[i] ? "Admin-badgeOk" : "Admin-badgeBad")
                    }
                  >
                    <span className="Admin-badgeDot" />
                    {dataObj[i] ? "True" : "False"}
                  </span>
                </div>
              </div>,
            );
          } else if (i === "organizationType") {
            fields.push(
              <div className="Admin-field" key={i}>
                <strong>{languageObj.organizationType}</strong>
                <div className="Admin-fieldValue">
                  {languageObj.organizationTypeLabel[dataObj[i]] || dataObj[i]}
                </div>
              </div>,
            );
          } else if (i === "bankSettingStatus") {
            fields.push(
              <div className="Admin-field" key={i}>
                <strong>{languageObj[i]}</strong>
                <div className="Admin-fieldValue">
                  <label className="Admin-toggle">
                    <input
                      type="checkbox"
                      defaultChecked={dataObj[i]}
                      onClick={onClickCheckBox}
                    />
                    <span className="Admin-toggleTrack">
                      <span className="Admin-toggleThumb" />
                    </span>
                  </label>
                </div>
              </div>,
            );
          } else {
            fields.push(
              <div className="Admin-field" key={i}>
                <strong>{languageObj[i]}</strong>
                <div className="Admin-fieldValue">{dataObj[i]}</div>
              </div>,
            );
          }
        });
      } else if (key !== "organization" && key !== "clicked") {
        fields.push(
          <div className="Admin-field" key={key}>
            <strong>{languageObj[key]}</strong>
            <div className="Admin-fieldValue">{item[key]}</div>
          </div>,
        );
      }
    });

    const statusKey = statusKeyOf(item.organizationObj.organizationPermitted);
    const subDomainValue =
      item.organizationObj.subDomain || item.subDomain || "";
    const agreementDateText = formatDate(item.organizationObj.agreementDate);
    const plugState = plugListingStateOf(item.organizationObj);
    const plugLabels = languageObj.plugListing;

    function renderPlugSection() {
      const isApproved = statusKey === "approved";
      const orgEnabled = orgEnabledPlug(item.organizationObj);

      return (
        <div className="Admin-section" key="plugListing">
          <h3>{plugLabels.title}</h3>
          <div className="Admin-plugPanel">
            <div className="Admin-plugStateRow">
              <span className={"Admin-badge " + plugBadgeClass(plugState)}>
                <span className="Admin-badgeDot" />
                {plugLabels.stateLabel[plugState]}
              </span>
              <span className="Admin-plugStateHint">
                {plugLabels.stateHint[plugState]}
              </span>
            </div>

            <div className="Admin-plugControls">
              <div className="Admin-field">
                <strong>{plugLabels.eligibleLabel}</strong>
                <div className="Admin-fieldValue">
                  <label
                    className={
                      "Admin-toggle" + (isApproved ? "" : " Admin-toggleOff")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={!!item.organizationObj[PLUG_ELIGIBLE_FIELD]}
                      disabled={!isApproved}
                      onChange={onPlugEligibleToggle}
                    />
                    <span className="Admin-toggleTrack">
                      <span className="Admin-toggleThumb" />
                    </span>
                  </label>
                </div>
                <div className="Admin-plugFieldHint">
                  {isApproved
                    ? plugLabels.eligibleHint
                    : plugLabels.eligibleBlockedHint}
                </div>
              </div>

              {/* The nonprofit's own switch — on unless it withdraws. Read
                  only here: staff can list an organization, but must not be
                  able to overrule its withdrawal. */}
              <div className="Admin-field">
                <strong>{plugLabels.optInLabel}</strong>
                <div className="Admin-fieldValue">
                  <span
                    className={
                      "Admin-badge " +
                      (orgEnabled ? "Admin-badgeOk" : "Admin-badgeWarn")
                    }
                  >
                    <span className="Admin-badgeDot" />
                    {orgEnabled ? plugLabels.optInYes : plugLabels.optInNo}
                  </span>
                </div>
                <div className="Admin-plugFieldHint">
                  {orgEnabled
                    ? plugLabels.optInHint
                    : plugLabels.optInDisabledHint}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    function renderDetailBody() {
      return (
        <React.Fragment>
          <div className="Admin-detailTop">
            <div>
              <h1 className="Admin-orgName">
                {item.organizationObj.organizationName}
              </h1>
              <div className="Admin-detailMeta">
                {statusKey === "pending" ? (
                  <span className={"Admin-badge " + statusBadgeClass(statusKey)}>
                    <span className="Admin-badgeDot" />
                    {languageObj.statusLabel.pending}
                  </span>
                ) : (
                  <div
                    className={"Admin-statusSelect " + statusBadgeClass(statusKey)}
                  >
                    <span className="Admin-badgeDot" />
                    <select
                      className="Admin-statusSelectInput"
                      value={statusKey}
                      onChange={onStatusChange}
                    >
                      <option value="pending">
                        {languageObj.statusLabel.pending}
                      </option>
                      <option value="approved">
                        {languageObj.statusLabel.approved}
                      </option>
                      <option value="declined">
                        {languageObj.statusLabel.declined}
                      </option>
                    </select>
                    <span className="Admin-arrow Admin-statusSelectArrow" />
                  </div>
                )}
                {subDomainValue && (
                  <span className="Admin-chip">
                    <code>
                      {subDomainValue}.{MAIN_DOMAIN}
                    </code>
                  </span>
                )}
                {agreementDateText && (
                  <span className="Admin-chip">
                    {languageObj.agreementDate}: {agreementDateText}
                  </span>
                )}
                {/* Only meaningful once an org is approved — before that the
                    answer is always "unavailable", which is noise on every
                    pending row. The section below still explains it. */}
                {statusKey === "approved" && (
                  <span className={"Admin-badge " + plugBadgeClass(plugState)}>
                    <span className="Admin-badgeDot" />
                    {plugLabels.chipPrefix} {plugLabels.stateLabel[plugState]}
                  </span>
                )}
              </div>
            </div>
            {statusKey === "pending" && (
              <div className="Admin-detailActions">
                <button
                  type="button"
                  className="Admin-btn Admin-btnDanger"
                  onClick={onDeclineClick}
                >
                  {languageObj.decline}
                </button>
                <button
                  type="button"
                  className="Admin-btn Admin-btnPrimary"
                  onClick={onApproveClick}
                >
                  <svg className="Admin-btnIcon" viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                  {languageObj.approve}
                </button>
              </div>
            )}
          </div>

          <div className="Admin-renderContent">{fields}</div>
          {reasonEl}
          {renderPlugSection()}
          {func.renderOrgDocs(item.id)}
        </React.Fragment>
      );
    }

    function renderOrgMobile() {
      function backBtn() {
        func.resetItemsColor();
      }

      return (
        <div className="Admin-detailsMobile">
          <div className="Admin-mobileHeader">
            <div className="Admin-backContainer">
              <div className="Admin-back" onClick={backBtn}>
                <div className="Admin-backBtn"></div>
                <div className="Admin-backText">{languageObj.backText}</div>
              </div>
            </div>
          </div>

          <div className="Admin-info">{renderDetailBody()}</div>
        </div>
      );
    }

    return (
      <div>
        {renderOrgMobile()}
        {renderDetailBody()}
      </div>
    );
  }

  renderErrMsg() {
    if (!this.state.errMsg) return;
    var acObject = [];
    let items = this.state.errMsg;

    Object.keys(items).forEach(function (i) {
      acObject.push(
        <div key={"key" + i + items["id"]}>{renderErrorMsg(items[i])}</div>,
      );
    });

    return <div>{acObject}</div>;
  }

  render() {
    if (!this.state.loaded)
      return (
        <div className="Admin-emptyPage">
          <div className="Admin-center">
            <div className="Admin-btm-load"></div>
          </div>
        </div>
      );

    const RenderOrg = (props) => {
      const data = props.data;
      const orgState = data.organizationObj.organizationPermitted;
      const statusKey = statusKeyOf(orgState);
      const itemClickStyle = data.clicked ? "Admin-postOn" : "Admin-post";
      const subText = [data.username, formatDate(data.organizationObj.agreementDate)]
        .filter(Boolean)
        .join(" · ");

      const onOrgClick = () => {
        let items = this.state.orgList;
        for (var i = 0; i < items.length; i++) {
          items[i].clicked = items[i].id === data.id ? true : false;
        }

        // Selecting an org should always work, even while a previous org's
        // files are still loading — fetchOrgFiles only applies the response
        // for the org selected last.
        this.setState({
          ...this.state,
          ...{
            orgList: items,
            docsErrMsg: null,
          },
        });
        this.fetchOrgFiles(data.id);
      };

      return (
        <div className={itemClickStyle} onClick={onOrgClick}>
          <div className="Admin-org">
            <div className="Admin-orgNameContainer">
              <div className="Admin-orgNameText">
                {data.organizationObj.organizationName}
              </div>
              {subText && <div className="Admin-orgSubText">{subText}</div>}
            </div>
            <div className="Admin-indicatorContainer">
              {plugListingStateOf(data.organizationObj) === "listed" && (
                <span className="Admin-plugTag">
                  {this.props.languageObj.plugListing.tag}
                </span>
              )}
              <div className={statusIndicatorClass(statusKey)} />
            </div>
          </div>
        </div>
      );
    };

    const selectMenu = this.props.languageObj.selectMenu;
    const sortOptions = this.props.languageObj.sortOptions;
    const languageObj = this.props.languageObj;
    const plugListingMenu = languageObj.plugListing.filterMenu;
    const visibleOrgs = this.getVisibleOrgs();

    const statusIconClass =
      this.state.selectedStatus === "approved"
        ? "Admin-selectStatusOk"
        : this.state.selectedStatus === "pending"
          ? "Admin-selectStatusWarn"
          : this.state.selectedStatus === "declined"
            ? "Admin-selectStatusBad"
            : "Admin-selectStatusAll";

    return (
      <div className="Admin-view">
        {this.state.fileViewerUrl && (
          <FileViewer
            url={this.state.fileViewerUrl}
            fileName={this.state.fileViewerName}
            onClose={() =>
              this.setState({ fileViewerUrl: null, fileViewerName: null })
            }
          />
        )}
        {this.state.showIdleWarning && (
          <div className="Admin-idleOverlay">
            <div className="Admin-idleModal">
              <h3>{languageObj.idleWarningTitle}</h3>
              <p>{languageObj.idleWarningMessage}</p>
              <div className="Admin-idleCountdown">
                {this.state.idleCountdown}s
              </div>
              <div className="Admin-idleActions">
                <button
                  type="button"
                  className="Admin-btn"
                  onClick={this.onIdleLogout}
                >
                  {languageObj.signOutNow}
                </button>
                <button
                  type="button"
                  className="Admin-btn Admin-btnPrimary"
                  onClick={this.onStaySignedIn}
                >
                  {languageObj.staySignedIn}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="Admin-headerContainer">
          <div
            className={
              "Admin-logo " +
              (PLATFORM === "plug" ? "Admin-plugLogo" : "Admin-checkPlusLogo")
            }
            onClick={this.onBackBtn}
          ></div>
          <div className="Admin-headerDivider" />
          <div className="Admin-crumb">{languageObj.title}</div>
          {this.state.fetching && (
            <div className="Admin-headerSpinner" />
          )}
          <div className="Admin-headerSpring" />
          <div className="Admin-headerActions">
            <div
              className="Admin-iconBtn Admin-languageContainer"
              title={languageObj.language}
            >
              <svg className="Admin-btnIcon" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              {renderOrgLanguageSelector()}
            </div>
            <div
              className="Admin-iconBtn Admin-logoutContainer"
              onClick={this.onLogoutClick}
              title={languageObj.logout}
            >
              <div className="Admin-logoutIcon"></div>
            </div>
          </div>
        </div>

        <div className="Admin-main">
          <div className="Admin-leftColumn">
            <div className="Admin-listHead">
              <div className="Admin-listTitleRow">
                <h2>{languageObj.title}</h2>
                <span className="Admin-listCount">
                  {visibleOrgs.length} {languageObj.totalLabel}
                </span>
              </div>
              <div className="Admin-search">
                <svg viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.2-3.2" />
                </svg>
                <input
                  className="Admin-searchInput"
                  type="text"
                  value={this.state.searchQuery}
                  placeholder={languageObj.searchPlaceholder}
                  onChange={this.onSearchChange}
                />
              </div>
              <div className="Admin-filters">
                <div className="Admin-selectWrap">
                  <div
                    className={"Admin-selectStatusIcon " + statusIconClass}
                  />
                  <select
                    className="Admin-selectMenu"
                    value={this.state.selectedStatus}
                    onChange={this.selectOnClick}
                  >
                    <option value="">{selectMenu[0]}</option>
                    <option value="approved">{selectMenu[1]}</option>
                    <option value="pending">{selectMenu[2]}</option>
                    <option value="declined">{selectMenu[3]}</option>
                  </select>
                  <div className="Admin-arrow" />
                </div>
                <select
                  className="Admin-sortMenu"
                  value={this.state.sortBy}
                  onChange={this.onSortChange}
                >
                  <option value="recent">{sortOptions[0]}</option>
                  <option value="name">{sortOptions[1]}</option>
                  <option value="status">{sortOptions[2]}</option>
                </select>
                <select
                  className="Admin-sortMenu Admin-plugFilterMenu"
                  value={this.state.plugListingFilter}
                  onChange={this.onPlugListingFilterChange}
                >
                  <option value="">{plugListingMenu.all}</option>
                  <option value="listed">{plugListingMenu.listed}</option>
                  <option value="ineligible">
                    {plugListingMenu.ineligible}
                  </option>
                  <option value="disabledByOrg">
                    {plugListingMenu.disabledByOrg}
                  </option>
                  <option value="blocked">{plugListingMenu.blocked}</option>
                </select>
              </div>
            </div>

            <div className="Admin-postContainer">
              {visibleOrgs.length === 0 ? (
                <div className="Admin-emptyList">{languageObj.noMatches}</div>
              ) : (
                visibleOrgs.map((data) => (
                  <RenderOrg data={data} key={"AdminrenderOrg" + data.id} />
                ))
              )}
            </div>
          </div>

          <div className="Admin-rightColumn">
            <div className="Admin-toprightColumn">
              {this.renderErrMsg()}
              {this.renderOrgDetails(this)}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Admin.propTypes = {
  logout: PropTypes.func,
  languageObj: PropTypes.object,
  language: PropTypes.string,
};

export default Admin;
